<template>
  <div>
    <p class="text-2xl mb-6">
      Список сборов
    </p>

    <div class="d-flex align-center flex-wrap mb-4">
      <span class="text-subtitle-1 font-weight-medium me-4 mb-2">Период:</span>
      <v-btn-toggle
        v-model="period"
        mandatory
        dense
        color="primary"
        class="mb-2"
        @change="loadDashboard"
      >
        <v-btn
          value="today"
          small
        >
          Сегодня
        </v-btn>
        <v-btn
          value="7d"
          small
        >
          7 дней
        </v-btn>
        <v-btn
          value="30d"
          small
        >
          30 дней
        </v-btn>
      </v-btn-toggle>
    </div>

    <v-row class="mb-6">
      <v-col
        cols="12"
        sm="6"
        md="4"
      >
        <v-card
          outlined
          class="pa-4 dashboard-card"
          :class="{ 'dashboard-card--clickable': dashboard.new_reservations > 0 }"
          @click="openDetails('reservations')"
        >
          <div class="text-body-2 text--secondary mb-1">
            Новые заявки
          </div>
          <div class="text-h4 font-weight-bold">
            <template v-if="dashboardLoading">
              …
            </template>
            <template v-else>
              {{ dashboard.new_reservations }}
            </template>
          </div>
          <div
            v-if="!dashboardLoading && dashboard.new_reservations > 0"
            class="text-caption primary--text mt-2"
          >
            Посмотреть список
          </div>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="4"
      >
        <v-card
          outlined
          class="pa-4 dashboard-card"
          :class="{ 'dashboard-card--clickable': dashboard.new_payments > 0 }"
          @click="openDetails('payments')"
        >
          <div class="text-body-2 text--secondary mb-1">
            Новые оплаты
          </div>
          <div class="text-h4 font-weight-bold">
            <template v-if="dashboardLoading">
              …
            </template>
            <template v-else>
              {{ dashboard.new_payments }}
            </template>
          </div>
          <div
            v-if="!dashboardLoading"
            class="text-body-2 text--secondary mt-1"
          >
            {{ formatMoney(dashboard.new_payments_sum) }}
          </div>
          <div
            v-if="!dashboardLoading && dashboard.new_payments > 0"
            class="text-caption primary--text mt-1"
          >
            Посмотреть список
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card>
      <v-tabs
        v-model="eventsTab"
        background-color="transparent"
        class="px-2"
      >
        <v-tab>
          Текущие
          <v-chip
            x-small
            class="ml-2"
            color="primary"
            text-color="white"
          >
            {{ currentEvents.length }}
          </v-chip>
        </v-tab>
        <v-tab>
          Архив
          <v-chip
            x-small
            class="ml-2"
          >
            {{ archiveEvents.length }}
          </v-chip>
        </v-tab>
      </v-tabs>
      <v-data-table
        :key="'events-tab-' + eventsTab"
        :headers="headers"
        :items="activeEventsList"
        :loading="loader"
        loading-text="Загрузка данных"
        :items-per-page="eventsTab === 0 ? -1 : 25"
        :hide-default-footer="eventsTab === 0"
        sort-by="start"
        :sort-desc="eventsTab !== 0"
        class="elevation-0"
        :no-data-text="eventsTab === 0 ? 'Нет текущих сборов' : 'Архив пуст'"
      >
        <template #[`item.title`]="{ item }">
          <router-link
            :to="'/events/' + item.id"
            class="font-weight-semibold"
            @click.native="setEventData(item)"
          >
            {{ item.title }}
          </router-link>
        </template>
        <template #[`item.start`]="{ item }">
          {{ item.date }}
        </template>
        <template #[`item.participants_count`]="{ item }">
          <span class="font-weight-medium">{{ item.participants_count || 0 }}</span>
        </template>
        <template #[`item.rooms_free`]="{ item }">
          <span
            class="font-weight-medium"
            :class="{ 'error--text': (item.rooms_free || 0) === 0 && (item.rooms_total || 0) > 0 }"
          >
            {{ item.rooms_free || 0 }}
          </span>
          <span class="text--secondary"> / {{ item.rooms_total || 0 }}</span>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="detailsDialog"
      max-width="960"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ detailsTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="detailsDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="periodLabel">
          Период: {{ periodLabel }}
        </v-card-subtitle>
        <v-card-text class="pt-2">
          <v-data-table
            :headers="detailsHeaders"
            :items="detailsItems"
            :loading="detailsLoading"
            loading-text="Загрузка…"
            :items-per-page="25"
            class="elevation-0"
            mobile-breakpoint="0"
          >
            <template #[`item.event_title`]="{ item }">
              <span class="text-body-2">{{ item.event_title || '—' }}</span>
            </template>
            <template #[`item.pay_price`]="{ item }">
              {{ formatMoney(item.pay_price) }}
            </template>
            <template #[`item.price`]="{ item }">
              {{ formatMoney(item.price) }}
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn
                v-if="reservationLink(item)"
                :to="reservationLink(item)"
                small
                text
                color="primary"
                @click="detailsDialog = false"
              >
                Перейти
              </v-btn>
              <span
                v-else
                class="text--secondary text-caption"
              >—</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiClose } from '@mdi/js'
import Api from '@/api/api'
import { mapActions } from 'vuex'

const PERIOD_LABELS = {
  today: 'Сегодня',
  '7d': '7 дней',
  '30d': '30 дней',
}

export default {
  data() {
    return {
      events_list: [],
      loader: false,
      eventsTab: 0,
      period: 'today',
      dashboardLoading: false,
      dashboard: {
        new_reservations: 0,
        new_payments: 0,
        new_payments_sum: 0,
      },
      headers: [
        {
          text: 'Название',
          value: 'title',
          align: 'start',
        },
        {
          text: 'Даты',
          value: 'start',
        },
        {
          text: 'Участники',
          value: 'participants_count',
          align: 'end',
          width: '120',
        },
        {
          text: 'Свободные номера',
          value: 'rooms_free',
          align: 'end',
          width: '160',
        },
      ],
      detailsDialog: false,
      detailsSection: null,
      detailsLoading: false,
      detailsItems: [],
      icons: { mdiClose },
    }
  },
  computed: {
    periodLabel() {
      return PERIOD_LABELS[this.period] || this.period
    },
    nowMs() {
      return Date.now()
    },
    currentEvents() {
      return (this.events_list || []).filter(event => !this.isArchivedEvent(event))
    },
    archiveEvents() {
      return (this.events_list || []).filter(event => this.isArchivedEvent(event))
    },
    activeEventsList() {
      return this.eventsTab === 0 ? this.currentEvents : this.archiveEvents
    },
    detailsTitle() {
      if (this.detailsSection === 'payments') {
        return 'Новые оплаты'
      }
      return 'Новые заявки'
    },
    detailsHeaders() {
      if (this.detailsSection === 'payments') {
        return [
          { text: 'Дата оплаты', value: 'paid_at', width: '140' },
          { text: 'Клиент', value: 'user_name' },
          { text: 'Сбор', value: 'event_title' },
          { text: 'Сумма', value: 'price', width: '120' },
          { text: 'Способ', value: 'payment_method_display', width: '120' },
          { text: '', value: 'actions', sortable: false, width: '100' },
        ]
      }
      return [
        { text: 'Дата', value: 'date', width: '140' },
        { text: 'Клиент', value: 'user_name' },
        { text: 'Сбор', value: 'event_title' },
        { text: 'Сумма', value: 'pay_price', width: '120' },
        { text: '', value: 'actions', sortable: false, width: '100' },
      ]
    },
  },
  mounted() {
    this.get_all_events()
    this.loadDashboard()
  },
  methods: {
    ...mapActions(['addEventnData']),
    isArchivedEvent(event) {
      const endRaw = event.end || event.start
      if (!endRaw) {
        return false
      }
      const endMs = new Date(endRaw).getTime()
      if (Number.isNaN(endMs)) {
        return false
      }
      return endMs < this.nowMs
    },
    formatMoney(value) {
      const num = Number(value) || 0
      return `${num.toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} ₽`
    },
    setEventData(event) {
      this.addEventnData(event)
    },
    reservationLink(item) {
      if (this.detailsSection === 'payments') {
        if (item.event_id && item.reservation_uuid) {
          return `/events/${item.event_id}/${item.reservation_uuid}`
        }
        return null
      }
      if (item.event_id && item.uuid) {
        return `/events/${item.event_id}/${item.uuid}`
      }
      return null
    },
    async get_all_events() {
      this.loader = true
      try {
        const events = await Api.getEvents()
        this.events_list = events || []
      } catch (e) {
        console.log('error', e)
      } finally {
        this.loader = false
      }
    },
    async loadDashboard() {
      this.dashboardLoading = true
      try {
        const data = await Api.getEventsDashboard({ period: this.period })
        this.dashboard = {
          new_reservations: data.new_reservations || 0,
          new_payments: data.new_payments || 0,
          new_payments_sum: data.new_payments_sum || 0,
        }
      } catch (e) {
        console.log('dashboard error', e)
        this.dashboard = {
          new_reservations: 0,
          new_payments: 0,
          new_payments_sum: 0,
        }
      } finally {
        this.dashboardLoading = false
      }
    },
    async openDetails(section) {
      const count = section === 'payments'
        ? this.dashboard.new_payments
        : this.dashboard.new_reservations
      if (!count || this.dashboardLoading) {
        return
      }

      this.detailsSection = section
      this.detailsDialog = true
      this.detailsLoading = true
      this.detailsItems = []
      try {
        const data = await Api.getEventsDashboardDetails({
          period: this.period,
          section,
        })
        this.detailsItems = data.items || []
      } catch (e) {
        console.log('dashboard details error', e)
        this.detailsItems = []
      } finally {
        this.detailsLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:hover {
    border-color: var(--v-primary-base, #9155fd);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}
</style>
